import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import MovieCard from '../components/MovieCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Browse = () => {
  const [movies, setMovies] = useState([]);
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [sortBy, setSortBy] = useState('popularity.desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGenres();
  }, []);

  useEffect(() => {
    fetchMovies();
  }, [selectedGenre, selectedYear, sortBy, page]);

  const fetchGenres = async () => {
    try {
      const response = await axios.get(`${API}/genres`);
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const fetchMovies = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('sort_by', sortBy);

      if (selectedGenre && selectedGenre !== 'all') {
        params.append('genre_id', selectedGenre);
      }
      if (selectedYear && selectedYear !== 'all') {
        params.append('year', selectedYear);
      }

      const response = await axios.get(`${API}/movies/discover?${params.toString()}`);
      setMovies(response.data.results || []);
    } catch (error) {
      console.error('Error fetching movies:', error);
      toast.error('Failed to load movies');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSelectedGenre('');
    setSelectedYear('');
    setSortBy('popularity.desc');
    setPage(1);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 max-w-7xl mx-auto pb-20" data-testid="browse-page">
      <div className="mb-12">
        <h1 className="font-anton text-5xl md:text-7xl text-white uppercase tracking-tight mb-4">
          Browse Trailers
        </h1>
        <p className="text-base md:text-lg text-gray-400">
          Filter by genre, year, and popularity
        </p>
      </div>

      {/* Filters */}
      <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-xl p-6 mb-12" data-testid="filter-section">
        <div className="flex items-center gap-3 mb-6">
          <Filter className="w-5 h-5 text-[#7FFF7F]" />
          <h2 className="text-xl font-semibold text-white">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm text-gray-400 mb-2 block">Genre</label>
            <Select value={selectedGenre || undefined} onValueChange={setSelectedGenre}>
              <SelectTrigger className="bg-black/60 border-white/20" data-testid="genre-filter">
                <SelectValue placeholder="All Genres" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/20">
                <SelectItem value="all">All Genres</SelectItem>
                {genres.map((genre) => (
                  <SelectItem key={genre.id} value={genre.id.toString()}>
                    {genre.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Year</label>
            <Select value={selectedYear || undefined} onValueChange={setSelectedYear}>
              <SelectTrigger className="bg-black/60 border-white/20" data-testid="year-filter">
                <SelectValue placeholder="All Years" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/20 max-h-60">
                <SelectItem value="all">All Years</SelectItem>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm text-gray-400 mb-2 block">Sort By</label>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-black/60 border-white/20" data-testid="sort-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-white/20">
                <SelectItem value="popularity.desc">Most Popular</SelectItem>
                <SelectItem value="popularity.asc">Least Popular</SelectItem>
                <SelectItem value="vote_average.desc">Highest Rated</SelectItem>
                <SelectItem value="vote_average.asc">Lowest Rated</SelectItem>
                <SelectItem value="release_date.desc">Newest</SelectItem>
                <SelectItem value="release_date.asc">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full border-white/20 hover:bg-white/10 text-white"
              onClick={handleReset}
              data-testid="reset-filters-button"
            >
              Reset Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Movies Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#7FFF7F] mx-auto mb-4"></div>
            <p className="text-gray-400">Loading movies...</p>
          </div>
        </div>
      ) : movies.length > 0 ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6 mb-12" data-testid="movies-grid">
            {movies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              className="border-white/20 hover:bg-white/10 text-white"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              data-testid="prev-page-button"
            >
              <ChevronLeft className="w-5 h-5 mr-2" />
              Previous
            </Button>
            <span className="text-white font-semibold px-6" data-testid="page-number">
              Page {page}
            </span>
            <Button
              variant="outline"
              className="border-white/20 hover:bg-white/10 text-white"
              onClick={() => setPage(page + 1)}
              data-testid="next-page-button"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </>
      ) : (
        <div className="text-center py-20">
          <p className="text-gray-400 text-lg">No movies found with these filters</p>
        </div>
      )}
    </div>
  );
};

export default Browse;